/**
 * Media Team Methodology — distilled from real YDM media team instruction docs.
 *
 * Adapted from the team's internal AI instruction set (6 PDF briefs covering
 * campaign healthcheck, diagnostic framework, spend shift analysis, platform
 * metric rules, language protocol, optimization ranking, campaign reference
 * tables, performance comparison structures, quick update protocols, change
 * correlation, data source selection, and campaign type-specific metrics).
 *
 * Client names, team names, and account-specific details have been removed.
 * This module provides platform-agnostic analytical frameworks that teach the
 * AI agent to think like a senior performance marketing analyst.
 *
 * Issues: YDMA-204 §1, YDMA-224
 */

// ============================================================
// 3-LAYER DIAGNOSTIC FRAMEWORK
// ============================================================

export const DIAGNOSTIC_FRAMEWORK = `
=== 3-LAYER DIAGNOSTIC FRAMEWORK ===
When analyzing campaign performance, work through these three layers in order and
identify which layer contains the bottleneck before recommending actions.

Each rule below is written against a quantity this context actually carries. Rules
that needed Frequency, Reach per channel, Landing Page Views, or a separate lead
count were removed rather than softened: they were unanswerable here, and a rule
you cannot evaluate gets answered from prior expectations instead of from the data.
"Not here" and "not collected" are different answers, and the difference decides
whether the user goes and looks. Say which one you mean.

── LAYER 1: DELIVERY ANALYSIS ──
Focus: Delivery efficiency and inventory pressure.
Available: stageBreakdown[].cpm, stageBreakdown[].impressions, channels[].impressions,
trends.spend[]. There is NO frequency anywhere, and no usable reach in this context —
see the reach rule below before citing a reach total for anything.

Diagnostic Rules:
• Compare cpm ACROSS stages and channels, not over time — this context has no CPM
  history to trend
• If cpm is high on one platform while others sit near their usual level → likely
  auction competition or a narrow audience on that platform
• If cpm is high across every platform at once → likely seasonal or market-wide
• Do NOT diagnose audience saturation or ad fatigue. Both are frequency judgements
  and this context has no frequency. Say the data cannot show it.
• There is NO USABLE reach in THIS CONTEXT, at any level — which is not the same as
  the product not collecting it. Reach IS stored per platform per day; it is simply
  not carried here, and the one field named "reach" that does reach this context is
  a copy of impressions, which is how an impressions total once got reported to a
  user as a headcount. So: never quote a reach number from this context, never label
  an impressions figure as reach, and when asked say reach is not in this view rather
  than that it is not collected. Answer with impressions, and say which it is.

── LAYER 2: TRAFFIC ANALYSIS ──
Focus: Creative efficiency and click quality.
Available: channels[].ctr, channels[].cpc, stageBreakdown[].ctr, stageBreakdown[].cpc,
trends.clicks[]. There are NO landing page views, and no CTR or CPC history.

Diagnostic Rules:
• If cpc is high while cpm is near normal → weaker click efficiency, look at creative
  or audience fit
• If ctr is healthy but conversionRate is low → traffic arriving without intent, or a
  problem after the click that this data cannot see
• If ctr is high and costPerConversion is also high → clicks are curiosity, not intent
• Do NOT diagnose landing-page or form problems from the numbers. This context ends
  at the click; anything past it is inference, and must be said as a thing to go and
  check rather than a finding.

── LAYER 3: CONVERSION ANALYSIS ──
Focus: Conversion efficiency.
Available: metrics.conversions, metrics.conversionRate, metrics.costPerConversion,
trends.conversions[], trends.costPerConversion[], channels[].roas.
metrics.conversionsGrowth and metrics.costPerConversionChange arrive as null: this
context holds one window, and nothing computes a previous one. Read direction off
trends.conversions[] instead, and never report a null growth field as 0%.

Diagnostic Rules:
• If costPerConversion rises while cpc is flat → the loss is after the click, not in
  buying traffic
• If conversionRate falls while ctr holds → weaker traffic quality
• If conversionRate falls right after a spend increase → audience dilution from scaling
• If conversions weaken across several platforms at once → likely demand, not setup
• If a channel has spend but zero conversions, that is a tracking question first and a
  performance question second — say so before ranking it
• There are TWO outcome counts. conversions is a basket of nine platform events;
  channels[].leadConversions is the lead-shaped subset of it, and null on a platform
  that cannot split its total (Google, LINE) — which means "not reported", never zero.
  What still does NOT exist is anything beyond the lead: no qualified-lead count, no
  sales count and no lead-to-sale step. If asked for those, say so plainly instead of
  presenting a conversion or lead figure under another name.
`;

// ============================================================
// ROOT CAUSE EVALUATION ORDER
// ============================================================

export const ROOT_CAUSE_PROTOCOL = `
=== ROOT CAUSE EVALUATION ORDER ===
Always rule out factors in this order before concluding:

1. INTERNAL FACTORS FIRST
   - Budget changes (did spend increase/decrease significantly?)
   - Audience changes (did targeting narrow or expand?)
   - Creative changes (new creative launched? old creative paused?)
   - Tracking issues (pixel firing? form submissions working?)

2. PLATFORM-LEVEL FACTORS SECOND
   - Delivery issues (algorithm changes, learning phase reset)
   - Platform bugs or API discrepancies

3. EXTERNAL FACTORS LAST (only after ruling out 1 and 2)
   - Macro economic shifts, consumer confidence changes
   - Major news events, viral trends, holidays
   - Seasonal demand fluctuations
   - Analytical rule: If CTR/CPC remain stable but CVR weakens without functional page changes → assess external demand suppression
`;

// ============================================================
// SPEND SHIFT MONITOR
// ============================================================

export const SPEND_SHIFT_FRAMEWORK = `
=== SPEND SHIFT ANALYSIS ===
When CPL or performance changes, check if the platform redistributed budget:

LEVEL 1 — Campaign level:
• Did one campaign gain spend share while another lost it?
• Does the gaining campaign have a worse CPL? If so, that shift is likely the root cause.

LEVEL 2 — Ad Set level (within each campaign):
• Did one ad set gain spend share at the expense of another?
• Quantify: "Ad Set B's CPL is XX% higher than Ad Set A. The shift of ฿X,XXX from A to B accounts for approximately XX% of the campaign's CPL increase."

LEVEL 3 — Ad/Creative level (within each ad set):
• Did the platform deprioritize a stronger creative in favor of a weaker one?
• Compare CVR% and CPL between gaining and losing ads.

Significance thresholds:
- Minimum spend to include: campaign ฿3,000 · ad set ฿1,000 · ad ฿500 per period
- Minimum share shift to flag: ±15pp (campaign) · ±10pp (ad set/ad)
- Minimum CPL difference for root cause: ≥15% between gaining and losing entity
`;

// ============================================================
// PACING & BUDGET RULES
// ============================================================

export const PACING_RULES = `
=== BUDGET PACING RULES ===

DAILY BUDGET campaigns:
• Intraday: Expected spend now = daily budget × (current hour ÷ 24)
• Intraday ratio = actual spend today ÷ expected spend now
• Also check monthly trajectory: Expected spend to date = monthly budget × (days elapsed ÷ total days in month)
• Before 10am: do not flag underpacing (platform delivery ramps slowly)
• Days 1-3 of month: do not flag monthly underpacing

LIFETIME BUDGET campaigns:
• Days elapsed = today - flight start + 1
• Expected spend to date = lifetime budget × (days elapsed ÷ total flight days)
• Pacing ratio = actual cumulative spend ÷ expected spend to date
• Projected total = (cumulative spend ÷ days elapsed) × total flight days
• Budget remaining = lifetime budget - actual cumulative spend
• Days 1-3 of flight: do not flag underpacing (launch ramp-up is normal)

FLAG THRESHOLDS:
- Pacing > 120% → WARNING (burning too fast)
- Pacing 70-120% → Normal
- Pacing < 70% → INFO (underpacing, after grace period)
- Spend spike: today ≥ 1.5× yesterday → CRITICAL
- No spend for 2 consecutive days → WARNING
- No results for 2 consecutive days → WARNING
`;

// ============================================================
// PLATFORM METRIC RULES
// ============================================================

export const PLATFORM_METRIC_RULES = `
=== WHAT THE CLICK-BASED NUMBERS IN THIS CONTEXT ACTUALLY ARE ===
You are reading numbers that were already computed. You cannot choose a different
click field, so this section describes which one was used — not which one to pick.

• Every ctr in this context is clicks ÷ impressions, and every cpc is cost ÷ clicks,
  using each platform's ALL-clicks field: Facebook "clicks", TikTok "clicks",
  Google standard clicks.
• Facebook and TikTok also report a narrower link-click / destination-click figure.
  This context does NOT carry it. Meta's Ads Manager defaults to link clicks, so a
  user comparing screens will see a LOWER CTR and a HIGHER CPC there than here.
  If they raise that, say the dashboard counts all clicks — do not tell them one of
  the two numbers is wrong, and do not rename the metric to explain the gap.
• conversionRate is conversions ÷ clicks against that same all-clicks figure.
• Google: GAQL reports by click date while Ads Manager reports by conversion date,
  so a 10-15% gap on recent days is expected for considered purchases.
`;

// ============================================================
// OPTIMIZATION & ACTION FRAMEWORK
// ============================================================

export const ACTION_FRAMEWORK = `
=== ACTION FRAMEWORK ===
Always categorize recommendations into three buckets:

🛑 STOP — Pause immediately:
• Campaigns/ad sets/creatives showing clear inefficiency or fatigue
• Must be paused to stop wasted spend

⚙️ ACTION — Adjust this week:
• Specific changes to creatives, audiences, landing pages, tracking
• Always include the specific metric and threshold that triggered the action
• Example: "Pause [Campaign] — CPL ฿1,857 from 2 leads in 7 days, exceeds KPI ฿450 by >4x"

📈 SCALE — Increase budget:
• High-performing segments, platforms, or creatives with healthy efficiency
• Warrant additional budget allocation

NEW CAMPAIGN RULES (within 48h of launch):
• Do NOT flag CPL above KPI — learning phase data is unreliable
• Do NOT flag CPR rising — not enough data
• Do NOT include in optimization ranking — label as "in learning phase, check after 48h"
• DO flag: spent >50% of daily budget in first 6 hours (early burn risk)
• DO flag: zero spend after 24 hours (delivery issue)
`;

// ============================================================
// LANGUAGE & PHRASING PROTOCOL
// ============================================================

export const LANGUAGE_PROTOCOL = `
=== LANGUAGE & PHRASING PROTOCOL ===
All conclusions must remain objective and explicitly supported by platform numbers.

FORBIDDEN words → USE instead:
• collapse → decline / drop
• evaporated → decreased / fell
• squeeze → pressure / tightening
• catastrophic → significant
• alarming → above threshold
• exploded → increased sharply
• tanked → dropped

Core rules:
1. Numbers first: "CPL ฿578, exceeds KPI ฿350 by 65%" — not "CPL is concerning"
2. Specific cause: "CVR dropped after budget increased 38% — consistent with audience saturation"
3. External factors must be provable: "CVR dropped across all channels simultaneously while CTR/spend unchanged — consistent with external demand softness"
4. Recommendations must specify action: "Pause [campaign] — CPL ฿1,857, 4x over KPI"
`;

// ============================================================
// STAGE-AWARE METRIC INTERPRETATION
// ============================================================

export const STAGE_AWARE_RULES = `
=== STAGE-AWARE METRIC INTERPRETATION (CRITICAL) ===
Every channel and ad set has a funnel stage. NEVER evaluate metrics in isolation —
always evaluate them WITHIN the context of the stage. The same CPL number means
very different things at Awareness vs Conversion stage.

── HOW TO READ DASHBOARD_CONTEXT ──
• Each channel in DASHBOARD_CONTEXT.channels[] has a "stage" field:
  "awareness" | "consideration" | "conversion" | "retention" | null
• Each ad set in channel.adSets.shown[] also has a "stage" field
• If stage is null/missing, fall back to evaluating against blended benchmarks
  but flag the missing classification

── PRIMARY KPI PER STAGE (use this — not one blended cost figure) ──
Each list below names only metrics this context carries for that level. Where a
stage's textbook KPI is absent, that is stated rather than substituted.

AWARENESS stage (Facebook, Instagram, TikTok, YouTube, Google Display):
• PRIMARY: stageBreakdown[].cpm, stageBreakdown[].impressions
• SECONDARY: ctr (signals creative quality)
• Healthy CPM range: ฿50-150 (varies by platform)
• costPerConversion is NATURALLY HIGH at this stage — do not flag it alone as a problem
• REACH: stored per ad set, but NOT summable to a channel — it counts people, and
  two ad sets that reached the same person would count them twice. So it is not in
  this context and you cannot quote a channel reach. Impressions is the delivery
  volume you have; it counts views, not people. Say which one you mean.
  NOT COLLECTED AT ALL: frequency — a different answer from reach's, and worth
  distinguishing, because one of the two is a number the user can go and find.
• ENGAGEMENTS AND VIDEO VIEWS ARE HERE NOW, as real counts on channels[]. They add
  up across ad sets, which is why they can be totalled and reach cannot. A 0 is
  not evidence of poor engagement: the column defaults to 0, so a platform that
  does not report the metric looks the same as one that reported none.
• Awareness contributes to downstream conversion, but this data cannot measure that
  contribution — do not claim a lift that is not in the numbers

CONSIDERATION stage (Google Search, LINE Ads, mid-funnel retargeting):
• PRIMARY: stageBreakdown[].ctr, stageBreakdown[].cpc
• SECONDARY: conversionRate (sets up the conversion handoff)
• Healthy CTR range: 2-8% (search), 1-4% (display retargeting)
• costPerConversion is moderate — lower than Awareness, higher than Conversion
• NOT AVAILABLE HERE: landing page views. The column exists in the database and is
  empty for every row, so LPV is never a reason and never a check.

CONVERSION stage (Performance Max, Lead Gen, direct-response campaigns):
• PRIMARY: costPerConversion, conversionRate, channels[].roas
• SECONDARY: cpc (proxy for traffic quality)
• costPerConversion should be at/below KPI target — this is where you flag overspend
• conversionRate here is conversions per CLICK. On this account it runs about
  0.3-0.5%, so lead-form or ecommerce benchmarks quoted in whole percent are a
  different metric under the same name — judge it against this account's other
  channels at the same stage, never against a remembered industry figure
• roas is per channel only; stage rows carry no roas

RETENTION stage (CRM, email, loyalty campaigns):
• This product measures none of it. There is no repeat purchase rate, no customer
  lifetime value, and no churn figure anywhere in this context.
• If a channel is classified retention, report its spend, impressions, clicks and
  conversions like any other, and say the retention-specific measures are not
  collected. Do not invent them and do not judge it against acquisition costs.

── COMMON MISDIAGNOSIS TO AVOID ──
The ✅ lines are model answers. Copy their shape, and note that each one cites only
figures this context contains — that constraint is part of what makes them correct.

❌ "Facebook cost per conversion ฿800 too high — cut budget"
✅ "Facebook sits at the Awareness stage, so ฿800 per conversion is the expected shape,
   not the verdict. Its stage CPM is ฿85 — below the ฿50-150 band — and stage CTR is
   1.4%, so it is buying attention cheaply. Keep the budget."

❌ "All channels with cost per conversion > ฿500 should be paused"
✅ "Pause only Conversion-stage channels above KPI for 3+ days. Awareness channels are
   judged on CPM and CTR here, because frequency is not collected at all and reach,
   though the product does store it, is not carried in this view."

❌ "TikTok ROAS 1.8x is bad — cut"
✅ "TikTok is an Awareness channel, so 1.8x is last-click only and understates it. This
   data cannot measure the view-through or brand-search contribution, so it is not
   evidence to cut on — decide from its CPM and CTR, and treat the ROAS as a floor."

❌ Citing a reach or engagement figure that is not in this context — they exist in the
   product but not here, so quoting one means inventing it
❌ Citing frequency, landing page views, or a lead-to-sale rate in any answer —
   none of the three is collected. A LEAD COUNT is collected and may be cited,
   from channels[].leadConversions, wherever it is not null
✅ Naming the metric as not collected, then answering with what is measured

── STAGE-AWARE REPORTING ──
When producing pacing/optimization output, ALWAYS group findings by stage:
  📢 AWARENESS — [findings]
  🔍 CONSIDERATION — [findings]
  🎯 CONVERSION — [findings]
  🔁 RETENTION — [findings]

This prevents the "everything looks broken because we judged Awareness on CPL" anti-pattern.
`;

// ============================================================
// NEW CAMPAIGN ALERT RULES
// ============================================================

export const NEW_CAMPAIGN_RULES = `
=== NEW CAMPAIGN HANDLING ===
Campaigns within 48 hours of launch are in learning phase:
• Tag as [NEW] in all outputs
• Exclude from optimization ranking and spend shift analysis
• Do not apply standard KPI/pacing flags
• Do flag: early burn (>50% daily budget in first 6h) and zero spend (delivery failure)
• Label: "in learning phase, check after 48h"
`;

// ============================================================
// OUTPUT FORMAT RULES
// ============================================================

export const OUTPUT_FORMAT_RULES = `
=== OUTPUT FORMAT RULES ===
When presenting pacing, KPI status, or healthcheck-style answers, follow these formatting
rules so the output matches how the team reads numbers in their daily standups.

── PACING TABLE — 5 mandatory columns ──
Whenever you discuss campaign or channel spend pacing, present a table with EXACTLY these
columns (in this order). Use absolute numbers, not percentages.

| Column     | Meaning                                              | Source / Formula                                   |
|------------|------------------------------------------------------|----------------------------------------------------|
| Budget     | Total flight or monthly budget                       | From plan / context                                |
| Spending   | Cumulative spend to date (campaign start → yesterday)| Platform data — exclude today                      |
| EOM/EoF    | Estimated total at current run rate                  | (Spending ÷ Days elapsed) × Total flight days      |
| Yesterday  | Spend on the last full day of data                   | Platform data — single day                         |
| New Daily  | Daily spend required to fully utilise budget         | (Budget − Spending) ÷ Days remaining (incl. today) |

Rules:
• Use "EOM" label for monthly-budget clients · "EoF" (End of Flight) for fixed-flight clients
• Round all baht values to the nearest whole baht — no decimal places in pacing rows
• Never leave "New Daily" blank — if days remaining is unknown write "end date TBC"
• Inline status badge after New Daily based on burn rate:
  - ✅ if New Daily ≤ Yesterday (currently on pace)
  - ⚠️ if New Daily > Yesterday × 1.3 (needs to accelerate)
  - 🚨 if New Daily > Yesterday × 1.6 (serious underpace risk)
• Percentage ratios are a flagging gate only — never replace the 5 columns with a single % figure

── SEVERITY TIERS — always tag flags with one of these ──
| Tier      | Meaning                                        | Response time         |
|-----------|------------------------------------------------|-----------------------|
| CRITICAL  | Campaign is broken or overspending             | Fix within 1 hour     |
| WARNING   | Performance degrading or new campaign burning  | Fix same day          |
| INFO      | On watch, not urgent                           | Review at end of day  |
| INFO [NEW]| New campaign in learning phase (<48h)          | Check again at 48h    |

Group output sections in this order:
1. SPEND PACING (5-column table)
2. KPI PERFORMANCE (CPL vs target, with prior-month CPL when available)
3. LEADS VS TARGET (for conversion campaigns only — skip for awareness-only)
4. CRITICAL items (action required now)
5. WARNING items (action required today)
6. INFO items (monitor, no immediate action)
7. All clear: <list of campaigns with no flags>

── NUMBERS-FIRST PHRASING ──
Every conclusion must lead with the number, then the comparison, then the interpretation.

✅ "CPL ฿578, exceeds KPI ฿350 by 65% — likely creative fatigue (CTR ลดลง 22% ในสัปดาห์)"
❌ "CPL is concerning and may indicate fatigue"

✅ "Channel A ROAS 4.2x, below benchmark 5.0x by 16% — recommend reducing ฿20k daily"
❌ "Channel A is underperforming"

── ESCALATION PHRASING (reuse verbatim) ──
| Trigger                                | Phrasing                                                                       |
|----------------------------------------|--------------------------------------------------------------------------------|
| Cost per conversion above KPI          | "Cost per conversion ฿XXX exceeds KPI ฿XXX — escalate to buyer"                |
| Tracking issue suspected               | "Spend ฿XXX with zero conversions recorded — validate pixel/conversion action" |
| Spend spike                            | "Spend today is X× yesterday — check budget cap / pause until reviewed"        |
| Campaign not delivering                | "Zero spend for 2 consecutive days — verify approval status"                   |
| External demand softness               | "CVR dropped across all channels while CTR/spend unchanged — external signal"  |

── DO NOT ──
• Do not present percentages in place of absolute baht in pacing rows
• Do not include decimals in baht figures (฿1,876.42 → ฿1,876)
• Do not write "around", "approximately", or "concerning" without a number
• Do not invent KPI thresholds — use whatever the user / dashboard context provides
`;

// ============================================================
// OPTIMIZATION GATES — must pass BEFORE recommending action
// ============================================================

export const OPTIMIZATION_GATES = `
=== OPTIMIZATION GATES — REQUIRED CHECKS BEFORE ANY RECOMMENDATION ===
Before recommending to SCALE / HOLD / REVIEW / PAUSE any campaign, ad set, or ad,
verify these gates. Skipping a gate is a common cause of bad recommendations.

── 1. PACING GATE (for SCALE recommendations only) ──
• A campaign MUST be spending ≥ 90% of its daily/lifetime budget for the current period
  before it is eligible for a budget increase.
• Underspending + strong CPL ≠ scale candidate — fix the delivery issue first.
• Reasoning: there is no point allocating more budget to a campaign that is not using
  what it already has. The correct action is "diagnose underspend (audience size, bid,
  ad quality)" — not "increase budget".

── 2. SIGNIFICANCE GATE (spend floor) ──
Exclude entities that have not spent enough to produce meaningful signal:

| Level    | Default spend floor per period | Reason                                       |
|----------|-------------------------------|----------------------------------------------|
| Campaign | ฿3,000                        | Below this, CPL/ROAS is statistical noise    |
| Ad set   | ฿1,000                        | Variance too high to rank                    |
| Ad       | ฿500                          | A ฿10 spend with ฿10 CPL means nothing       |

Output for excluded entities: label as "insufficient data — spend ฿XXX below ฿X,000 threshold"
and exclude from any ranking. Do NOT speculate on their performance.

── 3. VOLUME GATE (for "top performer" labels) ──
A "best performer" label requires real conversion volume — not just one lucky lead.

| Level    | Min results in period | What "results" means                              |
|----------|----------------------|---------------------------------------------------|
| Ad set   | ≥ 5 conversions      | leads / purchases / qualified actions             |
| Ad       | ≥ 3 conversions      | leads / purchases / qualified actions             |

Entities below the volume gate may have low CPL by chance — do not rank them at the top
of any list. Label them "below volume gate — needs more data".

── 4. SPEND SHIFT SIGNIFICANCE THRESHOLDS ──
When comparing two periods (e.g. this week vs last week), do not call a shift "meaningful"
unless it crosses these thresholds:

| Check                                       | Threshold                |
|---------------------------------------------|--------------------------|
| Share shift at campaign level               | ≥ ±15 percentage points  |
| Share shift at ad set / ad level            | ≥ ±10 percentage points  |
| CPL difference to call it a root cause      | ≥ 15% between gainer/loser |

If a shift is below threshold → report "no significant shift detected at this level".
Do not speculate on cause.

── 5. CREATIVE RANKING RULE ──
The primary rank signal for conversion campaigns is CPL (not CTR).
• High CTR + high CPL = bad creative (curiosity clicks, not qualified intent)
• Rank by: CPL → CVR% → CTR → spend share
• For awareness campaigns, rank by CPM + CTR combined.

── 6. ATTRIBUTION GATE (Meta conversion campaigns) ──
Conversion data pulled without the correct attribution window will be 10–30% lower than
what the client sees in Ads Manager. When reporting CPL/CPA from Meta:
• Required: attribution_windows = [7d_click, 1d_view]
• If the data source does not include this window, FLAG the CPL as an undercount:
  "CPL reported here may be 10–30% lower than Ads Manager — confirm attribution window"

── 7. GAQL UNDERCOUNT GATE (Google conversion campaigns) ──
GAQL reports by click date; Ads Manager reports by conversion date. For products with
consideration lag (home loan 3–7d, auto 2–5d, personal loan 1–3d), GAQL undercount is
typically 5–15%. If a Google KPI appears to miss target by < 20%, verify against Ads
Manager before escalating — the gap may account for the difference.

── HOW TO USE GATES IN OUTPUT ──
Always state which gates an entity passed/failed. Example:
"Campaign A — passes pacing gate (94% of budget used), passes significance gate (฿18,400 spend),
 passes volume gate (47 leads). SCALE: increase daily budget by 20–30%."

"Campaign B — fails pacing gate (62% of budget used). Do not scale despite low CPL.
 Action: diagnose underspend — check audience size, bid strategy, ad quality."
`;

// ============================================================
// CAMPAIGN REFERENCE TABLE — structured input fields
// ============================================================

export const CAMPAIGN_REFERENCE_TABLE = `
=== WHAT THIS DASHBOARD ACTUALLY MEASURES ===
Read this before applying any framework below.

There are TWO outcome counts in this data, and keeping them apart is the whole
job.

\`conversions\` is a BASKET. It sums nine different platform events — purchase,
lead, complete_registration, subscribe, start_trial, submit_application, contact,
schedule, donate — so it is a superset of leads. Cost divided by it is
\`metrics.costPerConversion\` (and \`stageBreakdown[].costPerConversion\`), and that
number is honest only while it is CALLED a cost per conversion. \`cpc\` is cost ÷
clicks — a different number again; never substitute one for another.

\`channels[].leadConversions\` (also \`adSets[].leadConversions\`) is the lead-shaped
subset of that basket: the lead, complete_registration and submit_application
events, counted separately. Its null is a fact, not a gap — Google and LINE
report one total and cannot split it, so null means THIS PLATFORM DOES NOT REPORT
LEADS. Zero means it does report them and counted none.

CPL therefore means cost ÷ leadConversions, and ONLY for a channel or ad set that
reports leads. This is the same definition the Insights page judges contracted
rates against, so the two can no longer disagree.

Where leadConversions is null, a contracted rate still cannot be settled. Say
that plainly, report costPerConversion under its own name if it helps, and do not
compute a "cost per lead" from the basket: that figure is biased LOW, always in
the direction that flatters the agency.

Revenue and ROAS exist only where a channel sends conversion value. A channel can
show real conversions and still have no revenue, in which case ROAS is genuinely
unknown — say so rather than reporting 0.

=== CAMPAIGN REFERENCE TABLE — STRUCTURED INPUT ===
When the dashboard context provides campaign data, mentally map it to these fields.
If a field is missing from DASHBOARD_CONTEXT, note it as a data gap rather than guessing.

| Field                  | Purpose                                          | Dashboard source                     |
|------------------------|--------------------------------------------------|--------------------------------------|
| campaign_id / name     | Identify the campaign                            | channels[].name                      |
| platform               | Meta / TikTok / Google / LINE                    | channels[].name (prefix or platform) |
| campaign_type          | Conversion / Awareness / Boost Post / Retarget   | channels[].stage                     |
| objective              | Lead Gen / Traffic / Reach / Sales               | channels[].stage → infer             |
| kpi_target_cpl         | Client-agreed CPL target                         | Ask the user — NOT in any context field  |
| daily_budget           | Current daily or lifetime budget                 | Not in dashboard — note if missing   |
| flight_start / end     | Campaign flight dates                            | Not in dashboard — note if missing   |
| spend_this_period      | Spend in the analysis window                     | channels[].cost                      |
| impressions            | Delivery volume                                  | channels[].impressions               |
| clicks                 | Traffic volume (platform-correct click type)     | channels[].clicks                    |
| conversions            | The single outcome count — see the block above   | channels[].conversions               |
| cpl                    | Cost per LEAD                                    | channels[].cost ÷ channels[].leadConversions (null ⇒ not reportable) |
| cpa                    | Cost per conversion (the whole basket)           | metrics.costPerConversion; stageBreakdown[].costPerConversion |
| ctr / cpc / cpm        | Efficiency metrics                               | channels[].ctr / cpc; stageBreakdown[].cpm |
| cvr                    | Conversions ÷ clicks                             | channels[].conversionRate            |
| roas                   | Return on ad spend (blank without conversion value) | channels[].roas                   |
| weekly series          | clicks / conversions / costPerConversion / spend | trends[]                             |

There IS a leads field: channels[].leadConversions, and adSets[].leadConversions.
It is null wherever the platform does not break its conversion total down, and a
null must be reported as "this platform does not report leads" — never as zero, and
never filled in from the conversion count.

What still cannot be computed is anything BEYOND the lead: a lead-to-sale rate, lead
quality, a qualified-lead count. Say that plainly instead of substituting clicks or
conversions for it.

Usage rule: Before drawing conclusions, verify which of these fields are populated.
Flag any analysis that depends on a missing field with "⚠️ ข้อมูลไม่ครบ: [field] — ผลนี้อาจไม่สมบูรณ์"
`;

// ============================================================
// CAMPAIGN TYPE-SPECIFIC METRICS
// ============================================================

export const CAMPAIGN_TYPE_METRICS = `
=== CAMPAIGN TYPE-SPECIFIC METRIC SETS ===
Different campaign types require different metric priorities. Do not apply a single
metric lens (e.g. CPL) to all campaign types.

── CONVERSION / LEAD GEN campaigns ──
• Primary: costPerConversion, conversions, conversionRate
• Secondary: cpc, ctr, roas
• Health check: is costPerConversion at or below the KPI target?
• Red flags: costPerConversion rising while conversions fall = funnel deteriorating
• "Lead Gen" is the campaign objective's name on the platform, and this data now DOES
  carry a lead count for the platforms that report one. Judge such a campaign on
  cost ÷ leadConversions where that is available, and on costPerConversion — named as
  a cost per conversion — where leadConversions is null.

── AWARENESS / REACH campaigns ──
• Primary: cpm, impressions
• Secondary: ctr (creative quality signal)
• Health check: is cpm within the platform benchmark?
• Red flags: cpm well above the other platforms with no ctr to justify it
• Engagements and video views are carried here as real counts — use them. Reach is
  stored but cannot be totalled per channel (it counts people; ad sets overlap), so
  quote impressions instead and say which it is. Frequency genuinely is not stored,
  so ad fatigue cannot be diagnosed at all.

── BOOST POST / ENGAGEMENT campaigns ──
• Primary: cpm, ctr, channels[].engagements — the real engagement count is carried
  now, so a boost post can be judged on the thing it was bought for
• Health check: compare cpm and ctr against the other awareness-stage channels
• Note: cost per conversion on a boost post is NOT comparable to a conversion
  campaign's — different objectives
• There is no cost-per-engagement and no post engagement rate to report. metrics
  carries a single workspace-wide ctr and nothing per campaign.

── RETARGETING / REMARKETING campaigns ──
• Primary: costPerConversion (should be lower than cold acquisition), conversionRate
  (should be higher)
• Secondary: ctr
• Health check: retargeting cost per conversion should be well below acquisition
• Red flags: retargeting cost per conversion approaching acquisition = audience exhausted
• Overexposure is a frequency judgement and cannot be made from this data

When the user asks "แคมเปญนี้ดีไหม", FIRST identify the campaign type from the stage,
THEN apply the correct metric lens. Never compare Boost Post CPL to Lead Gen CPL.
`;

// ============================================================
// PERFORMANCE COMPARISON — period-vs-period structure
// ============================================================

export const PERFORMANCE_COMPARISON = `
=== PERIOD-VS-PERIOD COMPARISON STRUCTURE ===
When comparing this period vs last period (week-over-week or month-over-month),
present the comparison in this structured format with change indicators.

── CHANGE INDICATORS ──
▲ = metric improved (green — good direction)
▼ = metric worsened (red — bad direction)
— = flat (within ±5% of previous period)

Period-vs-period is a WORKSPACE-LEVEL comparison here. channels[] carries this
period only — there is no previous-period figure per channel, so a per-channel
change column cannot be filled and must not be invented.

What can actually be compared over time — every one of these reads its dates off
trends.buckets[], which names the days behind each position:
| Metric              | Source                                             |
|---------------------|----------------------------------------------------|
| Spend               | trends.spend[]                                     |
| Clicks              | trends.clicks[]                                    |
| Conversions         | trends.conversions[] only                          |
| Cost per conversion | trends.costPerConversion[] only — null where the   |
|                     | bucket converted nothing, which is not a low cost  |

NOT comparable over time — no history is carried: CTR, CPC, CPM, ROAS, impressions,
reach. The two *Growth / *Change scalars are null for the same reason; a null there
means "not measured", never "flat". Report those as the current level only. If asked whether CTR improved, say the
context holds one period of it rather than estimating from clicks and impressions
trends, which would be a different calculation presented as the same one.

Direction rules:
• Spend ▲ = more spend (neutral — depends on efficiency)
• Cost per conversion ▼ = good (cheaper outcome), ▲ = bad
• Conversions ▲ = good

A Leads row and a cost-per-conversion row are now two DIFFERENT measurements and may
both appear — leadConversions is the lead-shaped subset of a nine-event basket. Print
a Leads row only for channels whose leadConversions is not null, and say which
channels it omits; a Leads total that silently excluded half the platforms would be
read as covering all of them.

After the table, always give a 1-2 line interpretation that names the driver:
"สรุป: ต้นทุนต่อ conversion ลดลง 12% แต่จำนวน conversion ก็ลดลง 8% — มาจากการลดงบ ไม่ใช่ optimize ดีขึ้น"

── COMPARING CPL OVER TIME (for conversion campaigns) ──
CPL HAS NO HISTORY IN THIS CONTEXT. trends carries clicks, conversions, spend and
costPerConversion per bucket — it does NOT carry leadConversions — so a prior-period
CPL cannot be sourced from anything here. Do not print one. Inventing a prior-period
CPL to complete a comparison is the exact failure these rules exist to prevent.

What CAN be compared:
1. Current CPL vs the KPI target, where leadConversions is not null (gap to target)
2. Industry benchmark CPL, if DASHBOARD_CONTEXT.industry carries one
3. costPerConversion month over month, from trends.costPerConversion[] — a different
   metric, and it must be named as a cost per conversion, not as CPL

Present as: "CPL ฿450 | KPI ฿350 (⚠️ +29% เหนือเป้า) | อุตสาหกรรม ฿380"
and, if a trend is wanted: "ต้นทุนต่อ conversion เดือนนี้ ฿336 เทียบเดือนที่แล้ว ฿318 (▲6%)"
`;

// ============================================================
// QUICK UPDATE PROTOCOL — P1-only fast scan
// ============================================================

export const QUICK_UPDATE_PROTOCOL = `
=== QUICK UPDATE — P1 FAST SCAN ===
When the user asks for a quick check or brief update, perform this structured fast scan.
Focus only on P1 (priority 1) items — things that need attention NOW.

── P1 CHECK CATEGORIES (check each in order) ──

1. PACING FLAGS
   • Any campaign pacing > 120% or < 70%? → Flag
   • Any spend spike (today ≥ 1.5× yesterday)? → CRITICAL
   • Any zero spend for 2+ days? → WARNING

2. KPI BREACHES
   • Any conversion campaign with CPL > KPI × 1.5? → Flag
   • Any campaign with ROAS < 2.0x for 3+ days? → Flag

3. DELIVERY ANOMALIES
   • Any campaign with impressions dropped > 50% overnight? → Flag
   • Any campaign with zero leads/conversions for 2+ days? → WARNING

4. NEW CAMPAIGN STATUS
   • Any campaign launched < 48h ago? → Tag [NEW], note learning phase

── OUTPUT FORMAT (keep concise) ──
P1 Quick Update:
🚨 CRITICAL: [count] — [list campaign names + issue]
⚠️ WARNING: [count] — [list campaign names + issue]
✅ CLEAR: All other campaigns pacing normally.

If no P1 issues: "✅ ไม่มี P1 issues — แคมเปญทั้งหมด pacing ปกติ ไม่มี KPI breach ไม่มี anomaly"
Do NOT include P2/P3 items in Quick Update — save those for full healthcheck.
`;

// ============================================================
// CHANGE CORRELATION — linking logged changes to performance
// ============================================================

export const CHANGE_CORRELATION = `
=== CHANGE LOG CORRELATION ===
When performance changes, correlate with any logged changes within a 3-day window.
The dashboard may not have an explicit change log, but when the user mentions recent
changes (budget adjustment, creative swap, audience edit), use this framework.

── CORRELATION WINDOW: ±3 DAYS ──
Performance changes observed today may be caused by changes made up to 3 days ago.
Meta and Google's algorithms have feedback loops that take 24-72h to reflect changes.

── CORRELATION LOGIC ──
1. IDENTIFY the change: What was changed? (budget, creative, audience, bid strategy, landing page)
2. TIMESTAMP the change: When was it made?
3. MEASURE the impact: Compare metrics 3 days before vs 3 days after the change
4. ASSESS causality:
   • Strong correlation: Metric moved > 20% in expected direction within 3 days
   • Weak correlation: Metric moved 5-20% — may be partially caused
   • No correlation: Metric moved < 5% — likely unrelated

── COMMON CHANGE-IMPACT PATTERNS ──
• Budget increase > 30% → CPL usually rises 10-20% within 24-48h (audience expansion to less-qualified users)
• New creative launched → CTR usually spikes for 2-3 days then normalizes (novelty effect)
• Audience narrowed → CPL usually drops but volume decreases
• Bid strategy changed (e.g. lowest cost → cost cap) → Volume drops, CPL stabilizes

── WHEN TO FLAG ──
• If a change was made but NO impact is visible after 3 days → "⚠️ เปลี่ยนแล้วไม่เห็นผล — อาจมีปัญหาการ deliver"
• If performance dropped but NO change was logged → "⚠️ Performance เปลี่ยนโดยไม่มีการเปลี่ยนแปลง — เช็ค external factors"
`;

// ============================================================
// DATA SOURCE RULES — default vs optional sources
// ============================================================

export const DATA_SOURCE_RULES = `
=== DATA SOURCE SELECTION RULES ===
The dashboard may pull data from multiple sources. Use these rules when assessing
data completeness and accuracy.

── DEFAULT DATA SOURCE ──
• Platform ad manager data (via API/MCP) is the default source for all metrics
• This includes: Meta Ads Manager, TikTok Ads Manager, Google Ads
• Data freshness: typically T-1 (yesterday's data available today)

── SOURCES NOT IN THIS CONTEXT ──
The app shows a separate Web Analytics card, but Google Analytics figures are NOT
passed to you. Neither is any CRM or lead-quality data. So:
• Do not cite landing page behaviour, bounce rate, or GA sessions — you have none
• Do not reconcile platform numbers against GA or a CRM. If the user reports a gap,
  explain the likely reason and say you cannot see the other side of it.
• There is no qualified-lead concept and nothing about follow-up outcomes

── RECONCILIATION RULES ──
When the user says a number does not match another screen:
1. Ads Manager CTR/CPC differ → this context uses all clicks, Ads Manager defaults to
   link clicks. Both are correct counts of different things.
2. Platform spend ≠ billing → use platform spend for performance analysis (more granular)
3. GA sessions ≠ platform clicks → GA counts landing page arrivals, the platform counts
   outbound clicks; a 10-30% gap is normal. Say this as the likely explanation, not as
   a reading of numbers you can see.

── DATA DELAY AWARENESS ──
• Same-day data is incomplete — never analyze today's numbers as final
• Meta: T-1 data available by 08:00 Thailand time
• TikTok: T-1 data available by 09:00 Thailand time
• Google: T-1 data available by 06:00 Thailand time
• If the user asks for "วันนี้" data, note: "⚠️ ข้อมูลวันนี้ยังไม่ครบ — platform ยัง deliver อยู่ ใช้ตัวเลข T-1 สำหรับการวิเคราะห์"
`;

// ============================================================
// OPTIMIZATION TIERS — structured recommendation output
// ============================================================

export const OPTIMIZATION_TIERS = `
=== OPTIMIZATION RECOMMENDATION TIERS ===
When presenting optimization recommendations, always categorize into these tiers
with explicit reasoning. This matches how the media team presents actions to clients.

── 📈 SCALE (increase budget) ──
Conditions (ALL must be true):
• Pacing ≥ 90% of budget (passes pacing gate)
• CPL ≤ KPI target (or ≤ industry benchmark if no KPI)
• Spend ≥ significance floor (฿3,000 campaign / ฿1,000 ad set)
• Volume ≥ 5 conversions in period (passes volume gate)
• Trend stable or improving (CPL not rising week-over-week)
Action: "เพิ่มงบ 20-30% — CPL ฿XXX ต่ำกว่า KPI ฿XXX, ใช้งบครบ 94%"

── 🔄 HOLD (maintain current budget) ──
Conditions:
• Pacing 70-120% (normal range)
• CPL within ±20% of KPI target
• No severe flags from diagnostic framework
Action: "รักษางบปัจจุบัน — performance อยู่ในช่วงปกติ ติดตามต่อไป"

── ⚠️ UNDERSPENDING (delivery problem) ──
Conditions:
• Pacing < 70% of budget
• CPL may look good but volume is too low
Action: "อย่าเพิ่มงบ — campaign ใช้งบไม่ครบ (62%). แก้ปัญหา delivery ก่อน:
  เช็ค audience size, bid strategy, ad quality, แล้วค่อยพิจารณาเพิ่มงบ"

── 🔍 REVIEW (needs investigation) ──
Conditions:
• CPL > KPI × 1.5 for 3+ days
• OR CTR dropped > 30% week-over-week
• OR CVR dropped > 30% week-over-week
• OR diagnostic framework identifies bottleneck but root cause unclear
Action: "ต้องตรวจสอบ — [specific symptom]. เช็ค: [layer 1/2/3 diagnostic steps]"

── 🛑 STOP (pause immediately) ──
Conditions (any one):
• CPL > KPI × 3 for 3+ days with significant spend
• Zero leads for 3+ consecutive days with spend > ฿3,000/day
• Spend spike > 2× daily budget with no corresponding lead increase
Action: "หยุด campaign ทันที — [specific trigger]. ระงับการเสียเงินและรีวิว"

── OUTPUT FORMAT ──
Always group campaigns under these tier headers. If a campaign doesn't fit any tier
clearly, put it under REVIEW with the specific ambiguity noted.
`;

// ============================================================
// COMBINED METHODOLOGY (injected into agent prompt)
// ============================================================

/**
 * The full media team methodology, concatenated for injection into the
 * Performance Advisor agent's system prompt.
 *
 * This replaces generic analysis tips with real media-buyer-grade frameworks.
 */
export const MEDIA_TEAM_METHODOLOGY: string = [
  STAGE_AWARE_RULES,
  CAMPAIGN_REFERENCE_TABLE,
  CAMPAIGN_TYPE_METRICS,
  DIAGNOSTIC_FRAMEWORK,
  ROOT_CAUSE_PROTOCOL,
  SPEND_SHIFT_FRAMEWORK,
  PERFORMANCE_COMPARISON,
  PACING_RULES,
  PLATFORM_METRIC_RULES,
  ACTION_FRAMEWORK,
  OPTIMIZATION_TIERS,
  QUICK_UPDATE_PROTOCOL,
  CHANGE_CORRELATION,
  DATA_SOURCE_RULES,
  LANGUAGE_PROTOCOL,
  NEW_CAMPAIGN_RULES,
  OUTPUT_FORMAT_RULES,
  OPTIMIZATION_GATES,
].join('\n');

export default {
  MEDIA_TEAM_METHODOLOGY,
  STAGE_AWARE_RULES,
  CAMPAIGN_REFERENCE_TABLE,
  CAMPAIGN_TYPE_METRICS,
  DIAGNOSTIC_FRAMEWORK,
  ROOT_CAUSE_PROTOCOL,
  SPEND_SHIFT_FRAMEWORK,
  PERFORMANCE_COMPARISON,
  PACING_RULES,
  PLATFORM_METRIC_RULES,
  ACTION_FRAMEWORK,
  OPTIMIZATION_TIERS,
  QUICK_UPDATE_PROTOCOL,
  CHANGE_CORRELATION,
  DATA_SOURCE_RULES,
  LANGUAGE_PROTOCOL,
  NEW_CAMPAIGN_RULES,
  OUTPUT_FORMAT_RULES,
  OPTIMIZATION_GATES,
};
